import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Calendar, Users, Clock, MapPin, ChevronLeft,
  CheckCircle, Loader2, Video, BookOpen
} from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';

interface ScheduleItem {
  id: string;
  title: string;
  description: string | null;
  scheduled_date: string;
  scheduled_time: string | null;
  duration_minutes: number;
  event_type: string;
  meeting_url: string | null;
}

interface CohortData {
  id: string;
  name: string;
  description: string;
  track_name: string;
  track_slug: string;
  track_description: string;
  instructor_name: string;
  instructor_email: string;
  start_date: string;
  end_date: string | null;
  max_members: number | null;
  meeting_schedule: string | null;
  member_count: number;
  schedule: ScheduleItem[];
}

interface Member {
  user_id: string;
  display_name: string;
  email: string;
  role: string;
  joined_at: string;
}

export default function CohortDetail() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { fetchApi } = useApi();
  const [cohort, setCohort] = useState<CohortData | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEnrolled, setIsEnrolled] = useState(false);

  useEffect(() => {
    loadCohort();
  }, [id]);

  useEffect(() => {
    if (cohort && user) {
      checkEnrollment();
      loadMembers();
    }
  }, [cohort, user]);

  async function loadCohort() {
    try {
      setLoading(true);
      const data = await fetchApi<{ cohort: CohortData }>(`/api/cohorts/${id}`);
      setCohort(data.cohort);
    } catch (err) {
      setError('Failed to load cohort');
    } finally {
      setLoading(false);
    }
  }

  async function checkEnrollment() {
    try {
      const data = await fetchApi<{ cohorts: { id: string }[] }>('/api/cohorts/my/enrolled');
      setIsEnrolled(data.cohorts.some(c => c.id === id));
    } catch {
      // Not logged in or other error
    }
  }

  async function loadMembers() {
    try {
      const data = await fetchApi<{ members: Member[] }>(`/api/cohorts/${id}/members`);
      setMembers(data.members);
    } catch {
      // User may not have access to member list
    }
  }

  async function handleJoin() {
    if (!user) {
      window.location.href = '/login';
      return;
    }

    setJoining(true);
    setError(null);

    try {
      await fetchApi(`/api/cohorts/${id}/join`, { method: 'POST' });
      setIsEnrolled(true);
      loadMembers();
      if (cohort) {
        setCohort({ ...cohort, member_count: cohort.member_count + 1 });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to join cohort');
    } finally {
      setJoining(false);
    }
  }

  async function handleLeave() {
    setJoining(true);
    try {
      await fetchApi(`/api/cohorts/${id}/leave`, { method: 'POST' });
      setIsEnrolled(false);
      if (cohort) {
        setCohort({ ...cohort, member_count: cohort.member_count - 1 });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to leave cohort');
    } finally {
      setJoining(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-accent-blue" />
      </div>
    );
  }

  if (error || !cohort) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-6 text-center">
          <p className="text-red-400">{error || 'Cohort not found'}</p>
          <Link to="/community" className="text-accent-blue hover:underline mt-2 inline-block">
            ← Back to Community
          </Link>
        </div>
      </div>
    );
  }

  const isFull = Boolean(cohort.max_members && cohort.member_count >= cohort.max_members);
  const hasStarted = new Date(cohort.start_date) <= new Date();

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <Link
        to="/community"
        className="inline-flex items-center gap-1 text-gray-400 hover:text-white mb-6"
      >
        <ChevronLeft className="w-4 h-4" />
        Back to Community
      </Link>

      {/* Header */}
      <div className="bg-gray-800/50 rounded-lg p-6 mb-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <span className="text-xs px-2 py-1 bg-accent-blue/20 text-accent-blue rounded">
              {cohort.track_name}
            </span>
            <h1 className="text-2xl font-bold text-white mt-2">{cohort.name}</h1>
          </div>

          {!isEnrolled ? (
            <button
              onClick={handleJoin}
              disabled={joining || isFull}
              className={`
                px-6 py-2 rounded-lg font-medium transition-colors
                ${isFull
                  ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  : 'bg-accent-blue hover:bg-blue-500 text-slate-900'
                }
              `}
            >
              {joining ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : isFull ? (
                'Cohort Full'
              ) : (
                'Join Cohort'
              )}
            </button>
          ) : (
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1 text-green-400 text-sm">
                <CheckCircle className="w-4 h-4" />
                Enrolled
              </span>
              <button
                onClick={handleLeave}
                disabled={joining}
                className="text-sm text-gray-400 hover:text-red-400 transition-colors"
              >
                Leave
              </button>
            </div>
          )}
        </div>

        <p className="text-gray-300 mb-6">{cohort.description}</p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div className="flex items-center gap-2 text-gray-400">
            <Calendar className="w-4 h-4" />
            <span>
              {hasStarted ? 'Started' : 'Starts'}{' '}
              {new Date(cohort.start_date).toLocaleDateString()}
            </span>
          </div>
          {cohort.end_date && (
            <div className="flex items-center gap-2 text-gray-400">
              <Clock className="w-4 h-4" />
              <span>Ends {new Date(cohort.end_date).toLocaleDateString()}</span>
            </div>
          )}
          <div className="flex items-center gap-2 text-gray-400">
            <Users className="w-4 h-4" />
            <span>
              {cohort.member_count}{cohort.max_members ? ` / ${cohort.max_members}` : ''} members
            </span>
          </div>
          <div className="flex items-center gap-2 text-gray-400">
            <BookOpen className="w-4 h-4" />
            <Link to={`/tracks/${cohort.track_slug}`} className="hover:text-accent-blue">
              View Track
            </Link>
          </div>
        </div>

        {cohort.meeting_schedule && (
          <div className="mt-4 p-3 bg-gray-700/50 rounded-lg">
            <p className="text-sm text-gray-300">
              <MapPin className="w-4 h-4 inline mr-1" />
              Schedule: {cohort.meeting_schedule}
            </p>
          </div>
        )}

        {error && (
          <p className="mt-4 text-red-400 text-sm">{error}</p>
        )}
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {/* Schedule */}
        <div className="md:col-span-2">
          <h2 className="text-xl font-semibold text-white mb-4">Schedule</h2>

          {cohort.schedule.length === 0 ? (
            <div className="bg-gray-800/50 rounded-lg p-6 text-center text-gray-400">
              <Calendar className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No scheduled sessions yet.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {cohort.schedule.map(item => {
                const isPast = new Date(item.scheduled_date) < new Date();
                return (
                  <div
                    key={item.id}
                    className={`
                      bg-gray-800/50 rounded-lg p-4
                      ${isPast ? 'opacity-60' : ''}
                    `}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          {item.event_type === 'live' && (
                            <Video className="w-4 h-4 text-red-400" />
                          )}
                          <h3 className="font-medium text-white">{item.title}</h3>
                        </div>
                        {item.description && (
                          <p className="text-sm text-gray-400 mb-2">{item.description}</p>
                        )}
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span>
                            {new Date(item.scheduled_date).toLocaleDateString()}
                            {item.scheduled_time && ` at ${item.scheduled_time}`}
                          </span>
                          <span>{item.duration_minutes} min</span>
                        </div>
                      </div>
                      {item.meeting_url && isEnrolled && !isPast && (
                        <a
                          href={item.meeting_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm px-3 py-1 bg-accent-blue/20 text-accent-blue rounded hover:bg-accent-blue/30 transition-colors"
                        >
                          Join
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Members Sidebar */}
        <div>
          <h2 className="text-xl font-semibold text-white mb-4">
            Members ({cohort.member_count})
          </h2>

          <div className="bg-gray-800/50 rounded-lg p-4">
            {/* Instructor */}
            <div className="pb-3 mb-3 border-b border-gray-700">
              <p className="text-xs text-gray-500 uppercase mb-2">Instructor</p>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-accent-blue/20 flex items-center justify-center text-accent-blue font-medium">
                  {cohort.instructor_name.charAt(0)}
                </div>
                <span className="text-white">{cohort.instructor_name}</span>
              </div>
            </div>

            {/* Members list */}
            {members.length > 0 ? (
              <div className="space-y-2">
                <p className="text-xs text-gray-500 uppercase mb-2">Students</p>
                {members.slice(0, 10).map(member => (
                  <div key={member.user_id} className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-gray-700 flex items-center justify-center text-gray-400 text-xs">
                      {member.display_name.charAt(0)}
                    </div>
                    <span className="text-sm text-gray-300">{member.display_name}</span>
                  </div>
                ))}
                {members.length > 10 && (
                  <p className="text-xs text-gray-500 pt-2">
                    +{members.length - 10} more members
                  </p>
                )}
              </div>
            ) : isEnrolled ? (
              <p className="text-sm text-gray-400">Loading members...</p>
            ) : (
              <p className="text-sm text-gray-400">
                Join to see other members
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
